import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BackArrowIcon from '@assets/img/icons/arrow-back.svg?react';
import cn from 'classnames';

import { useGetBingoLeaderboardQuery } from '@/api/queries/bingo';
import { AppRoute } from '@/common/app/routes';
import { timeStringFormat } from '@/common/lib/timer';
import { type BingoRatingPrize } from '@/common/types/bingo';
import { type LeagueRewardType } from '@/common/types/league';
import { BingoRatingClaimModal } from '@/components/Bingo/BingoRatingClaimModal/BingoRatingClaimModal';
import { useDevDecoration } from '@/components/Leagues/PositionItem/decorations/useDevDecoration';
import { PositionItem } from '@/components/Leagues/PositionItem/PositionItem';
import { ResourceTitle } from '@/components/ResourceTitle/ResourceTitle';
import {
  H2,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { Loader } from '@/components/ui/Loader/Loader';
import { useAppSelector } from '@/redux/store';

import badgeLines from '/img/airdrop/badge_lines.png';

// BingoPrize структурно совпадает с `LeagueRewardType['rewards']` — нужно
// только дозаполнить отсутствующие ключи и отрезать decoration_id (он идёт
// отдельным prop'ом).
function prizeToRewards(
  prize: BingoRatingPrize | null | undefined,
): LeagueRewardType['rewards'] | undefined {
  if (!prize) return undefined;
  const hasAny = !!prize.wallet || !!prize.materials || !!prize.lottery;

  if (!hasAny) return undefined;

  return {
    wallet: prize.wallet ?? {},
    materials: prize.materials ?? {},
    lottery: prize.lottery,
  };
}

const BingoLeaderboard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetBingoLeaderboardQuery();
  const me = useAppSelector((state) => state.user.user);
  const myUserId = me?.id;
  const devDecorationId = useDevDecoration();

  const myRatingPrize = data?.myRatingPrize ?? null;
  const isUnclaimed = !!myRatingPrize && !myRatingPrize.claimed;
  const [claimModalOpen, setClaimModalOpen] = useState(false);

  // Авто-открытие модалки при заходе на страницу, если есть незабранный приз
  // (по аналогии с GlobalLeagues, где модалка открывается через context).
  useEffect(() => {
    if (isUnclaimed) setClaimModalOpen(true);
  }, [isUnclaimed]);

  // Локальный countdown для closesInSeconds
  const closesInitial = data?.ratingWindow?.closesInSeconds ?? 0;
  const [closesInSeconds, setClosesInSeconds] = useState(closesInitial);
  useEffect(() => {
    setClosesInSeconds(closesInitial);
  }, [closesInitial]);
  useEffect(() => {
    if (closesInitial <= 0) return;
    const tick = setInterval(() => {
      setClosesInSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);

    return (): void => clearInterval(tick);
  }, [closesInitial]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center pt-20">
        <Loader size={40} />
      </div>
    );
  }

  if (!data || data.entries.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center pt-20 px-4">
        <ResourceTitle>{t('Bingo Leaderboard')}</ResourceTitle>
        <StringMain className="!text-white/60 mt-8 text-center">
          {t('No participants yet')}
        </StringMain>
      </div>
    );
  }

  const isRatingWindow = data.phase === 'ratingWindow';

  // myRank/myScore — реальное место юзера в общем рейтинге (не ограничено
  // окном entries[]). null = ни одного спина в текущем событии. Если юзер
  // присутствует в entries[] (попал в видимое окно), отдельную строку не
  // дублируем — sticky-подсветка уже отрабатывает внутри списка.
  const meInEntries =
    !!myUserId && data.entries.some((e) => e.userId === myUserId);
  const showStandaloneMyRow = !meInEntries && data.myRank != null && !!me;

  return (
    <div
      className="flex flex-col gap-5 pb-6"
      style={{ paddingTop: 'var(--header-height)' }}
    >
      <div className="flex items-center gap-3 px-4">
        <button
          onClick={() =>
            navigate(isRatingWindow ? AppRoute.MAIN : AppRoute.BINGO)
          }
          className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 shrink-0"
        >
          <BackArrowIcon className="w-5 h-5" />
        </button>
        <ResourceTitle className="isolate !text-left">
          {t('Bingo Leaderboard')}
        </ResourceTitle>
      </div>

      {/* Rating window banner — when event has ended and rating is freezing */}
      {isRatingWindow && (
        <div className="relative mx-4">
          <div className="relative flex flex-col gap-2 overflow-hidden p-3 rounded-lg bg-[#FFC83A]/10 border border-[#FFC83A]/40">
            <H2 className="!text-[#FFC83A]">{t('Event ended')}</H2>
            <StringSmall className="!text-white/70">
              {t('Rating closes in')}:{' '}
              <span className="text-white font-bold">
                {timeStringFormat(closesInSeconds * 1000)}
              </span>
            </StringSmall>
            {isUnclaimed && (
              <button
                onClick={() => setClaimModalOpen(true)}
                className="mt-1 self-start text-sm font-bold underline text-[#FFC83A]"
              >
                {t('Open my prize')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scoring banner */}
      <div className="relative mx-4">
        <div className="relative flex flex-col gap-2 overflow-hidden p-3 rounded-lg bg-white/5 border border-white/10">
          <H2>{t('How scoring works')}</H2>
          <div className="flex flex-col gap-1">
            <StringSmall className="!text-white/60">
              <span className="text-white font-bold">+1</span>{' '}
              {t('point per opened cell')}
            </StringSmall>
            <StringSmall className="!text-white/60">
              <span className="text-white font-bold">+10</span>{' '}
              {t('points per completed line')}
            </StringSmall>
            <StringSmall className="!text-white/60">
              <span className="text-white font-bold">+50</span>{' '}
              {t('points per completed card')}
            </StringSmall>
          </div>
          <img
            src={badgeLines}
            alt=""
            className="absolute top-0 left-0 z-[-1] opacity-30"
          />
        </div>
      </div>

      {/* Winner reward banner */}
      <div className="relative mx-4">
        <div className="relative flex flex-col gap-1 overflow-hidden p-3 rounded-lg bg-[#4a1480]/15 border border-[#b73aa6]/40">
          <H2>{t('Winner reward')}</H2>
          <StringSmall className="!text-white/70">
            {t(
              'Only the #1 gets a unique skin that highlights their profile across all leaderboards.',
            )}
          </StringSmall>
          <StringSmall className="!text-white/70">
            {t(
              'Receiving the same skin again is exchanged for {{amount}} T-Gem.',
              { amount: 500 },
            )}
          </StringSmall>
        </div>
      </div>

      <div className="px-4">
        <div
          id="league-rank"
          className="w-full flex flex-col gap-1 mb-1 relative"
        >
          {data.entries.map((entry) => {
            const isMe = entry.userId === myUserId;

            return (
              <PositionItem
                key={entry.userId}
                position={entry.rank}
                avatar={entry.avatar}
                firstName={entry.firstName}
                lastName={entry.lastName}
                userName={entry.userName}
                level={entry.level}
                score={entry.score.toString()}
                isVip={entry.isVip}
                isUserPosition={isMe}
                decorationId={
                  isMe && devDecorationId
                    ? devDecorationId
                    : (entry.prize?.decoration_id ?? entry.decorationId)
                }
                rewards={prizeToRewards(entry.prize)}
                prizeSkinId={entry.prize?.decoration_id}
                style={isMe ? { top: 'var(--header-height)' } : undefined}
                className={cn(isMe && 'sticky bottom-0 z-[4]')}
              />
            );
          })}
          {showStandaloneMyRow && me && data.myRank != null && (
            <PositionItem
              key="me-standalone"
              position="50+"
              avatar={me.photoUrl}
              firstName={me.firstName}
              lastName={me.lastName}
              userName={me.userName}
              level={me.level}
              score={String(data.myScore ?? 0)}
              isVip={me.VIP}
              isUserPosition
              decorationId={devDecorationId ?? me.decorationId}
              style={{ top: 'var(--header-height)' }}
              className="sticky bottom-0 z-[4]"
            />
          )}
        </div>
      </div>

      <BingoRatingClaimModal
        isOpen={claimModalOpen}
        myRatingPrize={myRatingPrize}
        onClose={(): void => setClaimModalOpen(false)}
      />
    </div>
  );
};

export { BingoLeaderboard };
